// DB Viewer API client.

import { ApiError } from './client'

export interface DBInfo {
  service: string
  path: string
  size_mb: number
}

export interface TableInfo {
  name: string
  row_count: number
}

export interface QueryResult {
  columns: string[]
  rows: unknown[][]
  count: number
}

export interface ExecResult {
  rows_affected: number
  last_insert_id: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? res.statusText,
      body,
    )
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const dbApi = {
  databases: () => request<DBInfo[]>('/api/admin/db/databases'),

  tables: (service: string) =>
    request<TableInfo[]>(`/api/admin/db/${service}/tables`),

  query: (service: string, sql: string, limit = 100) =>
    request<QueryResult>(`/api/admin/db/${service}/query`, {
      method: 'POST',
      body: JSON.stringify({ sql, limit }),
    }),

  // execute runs a mutating statement. write_ack=true is required by the
  // backend so a stray call can't drop a table by accident — the UI sends
  // it only after the operator confirms the mutation in a dialog.
  execute: (service: string, sql: string) =>
    request<ExecResult>(`/api/admin/db/${service}/execute`, {
      method: 'POST',
      body: JSON.stringify({ sql, write_ack: true }),
    }),
}
